import { useWorkspaces } from "@/api/workspaces";
import { useDroppable } from "@dnd-kit/core";
import React, { useMemo } from "react";
import { classifyTransfer } from "./classifyTransfer";
import { dragActiveHasCanvas } from "./handlers/workspaceDetailDrop";
import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DroppableData,
} from "./ids";
import { cnTransferDropTargetSurface } from "./transferDropTargetStyles";

interface WorkspaceDropTargetProps {
  workspaceId: string;
  /** Folder path with trailing `/`, or `""` for workspace root. */
  parentPath?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * In-app drop target for the workspace main surface (root or current
 * folder). OS file uploads use `WorkspaceDropZone` separately.
 */
export function WorkspaceDropTarget({
  workspaceId,
  parentPath = "",
  children,
  className,
}: WorkspaceDropTargetProps) {
  const droppableData = useMemo((): DroppableData => {
    return {
      surface: "workspace-folder",
      destination: destinationFor("workspace-folder", {
        workspaceId,
        parentPath,
      }),
    };
  }, [workspaceId, parentPath]);

  // A workspace the user can only view accepts nothing (capabilities ride
  // on the workspace response; absence = allowed).
  const { data: allWorkspaces } = useWorkspaces();
  const canEditWorkspace =
    allWorkspaces?.find((ws) => ws.id === workspaceId)?.can_edit !== false;

  const { setNodeRef, isOver, active } = useDroppable({
    id: droppableId.workspaceFolder(workspaceId, parentPath),
    data: droppableData,
    disabled: !canEditWorkspace,
  });

  // Gate the highlight on what the drop would actually do, so the surface speaks
  // the same language as the pill: a real move/copy lights the active ring, an
  // invalid drop (a chat here, a canvas onto a folder path) shows the grayed
  // disabled ring, and a no-op shows nothing. Mixed workspace-detail selections
  // carry a `selectionSnapshot` with no single draggable — those keep the legacy
  // highlight (their validity is computed by the pill / dispatcher).
  const draggable = active ? asDraggableData(active.data.current) : null;
  let showOver = isOver;
  let disallowed = false;
  if (isOver) {
    if (draggable) {
      const classification = classifyTransfer(
        draggable,
        droppableData.destination,
      );
      if (classification.kind === "invalid") {
        disallowed = true;
      } else if (classification.kind === "noop") {
        showOver = false;
      }
    } else {
      disallowed = parentPath !== "" && dragActiveHasCanvas(active);
    }
  }

  return (
    <div
      ref={setNodeRef}
      className={cnTransferDropTargetSurface({
        isOver: showOver,
        disallowed,
        className,
      })}
    >
      {children}
    </div>
  );
}
